import {
    DirectionalLight,
    HemisphericLight,
    Light,
    PointLight,
    ShadowGenerator,
    ShadowLight,
    SpotLight,
} from '@babylonjs/core/Lights';
import { Color3, Quaternion, Vector3 } from '@babylonjs/core/Maths';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Scene } from '@babylonjs/core/scene';
import { DirectionalLightNode, HemisphericLightNode, PointLightNode, SpotLightNode, Token } from '@models/classes';
import { LightNode } from '@models/classes/lights/light-node';
import { ShadowLightNode } from '@models/classes/lights/shadow-light-node';
import { ITrackedClass } from '@models/interfaces/tracked-class';
import { createLightVisualization } from '@view/helpers/light-helper';
import { rotationToVector, toRadians } from '@view/helpers/math-helper';
import { KbViewer } from '@view/kb-viewer';
import { Renderer } from './renderer';
import { ShadowManager } from './shadow-manager';

type AnyLightNode = PointLightNode & DirectionalLightNode & HemisphericLightNode & SpotLightNode;

export class LightRenderer extends Renderer<LightNode, Light> {
    babylonLightPreviewsMap = new Map<string, Mesh>();
    gizmoLayer: UtilityLayerRenderer;

    modelToken = Token.LightNode;

    set showPreviews(show: boolean) {
        this.showAllLightPreviews = show;
        this.babylonMap.forEach(light => {
            this.updatePreviewMesh(light);
        });
    }

    protected showAllLightPreviews = false;

    constructor(viewer: KbViewer, scene: Scene, private shadowManager: ShadowManager) {
        super(viewer, scene);
        this.gizmoLayer = this.viewer.utilityLayer;
    }

    update3dInternal(node: ITrackedClass<LightNode>) {
        if (!node._parent && node._parentScene?.isNested()) {
            //don't render global lights in nested scenes
            return;
        }

        let light = this.get3d(node);
        const changes = this.getChanges(node) as Map<keyof AnyLightNode, any>;

        if (!light) {
            if (node instanceof PointLightNode) {
                light = new PointLight(node._dynamicId, node.position.toVec3(), this.scene);
            } else if (node instanceof HemisphericLightNode) {
                light = new HemisphericLight(node._dynamicId, node.direction.toVec3(), this.scene);
            } else if (node instanceof SpotLightNode) {
                light = new SpotLight(
                    node._dynamicId,
                    node.position.toVec3(),
                    node.direction.toVec3(),
                    (node.coneAngle * Math.PI) / 180,
                    2,
                    this.scene
                );
                (light as SpotLight).exponent = 2;
            } else if (node instanceof DirectionalLightNode) {
                light = new DirectionalLight(node._dynamicId, node.direction.toVec3(), this.scene);
            }

            this.set3d(node, light as Light);
        }

        const pLight = light as Light;

        if (changes.has('visible')) {
            // Can be falsy so need to check the map
            pLight.setEnabled(node.visible);
        }
        if (changes.has('intensity')) {
            // Can be falsy so need to check the map
            pLight.intensity = node.intensity;
        }
        if (changes.has('color')) {
            pLight.specular = node.color.toColor3();
            pLight.diffuse = node.color.toColor3();
        }
        if (changes.has('_parent')) {
            if (node._parent) {
                pLight.parent = this.viewer.getRendererNode(node._parent);
            } else {
                pLight.parent = null;
            }
        }

        let lightRotation: Quaternion;
        if (node.useEuler) {
            lightRotation = Quaternion.FromEulerAngles(
                toRadians(node.eulerRotation.x),
                toRadians(node.eulerRotation.y),
                toRadians(node.eulerRotation.z)
            );
        } else {
            lightRotation = Quaternion.RotationAxis(node.rotationAxis.toVec3(), toRadians(node.rotationAngle));
        }
        const rotationChanged = changes.hasAny('useEuler', 'eulerRotation', 'rotationAxis', 'rotationAngle');

        if (node instanceof PointLightNode) {
            const pLight = light as PointLight;
            if (changes.has('position')) {
                pLight.position = node.position.toVec3();
            }
        } else if (node instanceof HemisphericLightNode) {
            const pLight = light as HemisphericLight;
            if (changes.has('direction') || rotationChanged) {
                pLight.direction = this.rotateDirection(node.direction.toVec3(), lightRotation);
            }
            if (changes.has('groundColor')) {
                pLight.groundColor = node.groundColor.toColor3();
            }
        } else if (node instanceof SpotLightNode) {
            const pLight = light as SpotLight;
            if (changes.has('position')) {
                pLight.position = node.position.toVec3();
            }
            if (changes.has('direction') || rotationChanged) {
                pLight.direction = this.rotateDirection(node.direction.toVec3(), lightRotation);
            }
            if (changes.has('coneAngle')) {
                pLight.angle = (node.coneAngle * Math.PI) / 180;
            }
        } else if (node instanceof DirectionalLightNode) {
            const pLight = light as DirectionalLight;
            // pLight.position = new Vector3(0, 5, 0);
            if (changes.has('direction') || rotationChanged) {
                pLight.direction = this.rotateDirection(node.direction.toVec3(), lightRotation);
                //for directional lights, we hardcode the position to be negative direction for shadows
                pLight.position = pLight.direction.clone().negate();
            }
        }

        if (node instanceof ShadowLightNode) {
            const shadowLightNode = node as ITrackedClass<ShadowLightNode>;
            const shadowLight = light as ShadowLight;

            let sg = this.shadowManager.get(shadowLight);
            let justCreated = false;
            if (changes.has('generatesShadows')) {
                if (shadowLightNode.generatesShadows) {
                    sg = this.shadowManager.add(shadowLight, shadowLightNode.shadowResolution);
                    shadowLight.shadowEnabled = true;
                    justCreated = true;
                    changes.set('shadowFilter', shadowLightNode.shadowFilter);
                    changes.set('shadowQuality', shadowLightNode.shadowQuality);
                } else {
                    if (sg) {
                        shadowLight.shadowEnabled = false;
                        this.shadowManager.remove(shadowLight);
                        sg = undefined;
                    }
                }
            }

            //if the resolution is changed, and the shadow generator wasn't just created, we need to recreate it
            if (changes.has('shadowResolution') && sg && !justCreated) {
                this.shadowManager.remove(shadowLight);
                sg = this.shadowManager.add(shadowLight, shadowLightNode.shadowResolution);
                changes.set('shadowFilter', shadowLightNode.shadowFilter);
                changes.set('shadowQuality', shadowLightNode.shadowQuality);
                changes.set('processTransparency', shadowLightNode.processTransparency);
            }

            if (sg) {
                if (changes.has('shadowFilter')) {
                    sg.filter = ShadowManager.convertShadowFilter(shadowLightNode.shadowFilter);
                }

                if (changes.has('processTransparency')) {
                    sg.transparencyShadow = shadowLightNode.processTransparency;
                    sg.enableSoftTransparentShadow = shadowLightNode.processTransparency;
                }

                if (changes.has('shadowQuality')) {
                    sg.filteringQuality = (ShadowGenerator as any)[
                        `QUALITY_${shadowLightNode.shadowQuality.toUpperCase()}`
                    ];
                }

                //need to update projection matrix if direction has been changed
                if (changes.hasAny('direction', 'position')) {
                    this.shadowManager.clearProjectMatrixCache();
                }
            }
        }

        if (changes.has('_selected')) {
            if (!pLight.metadata) {
                pLight.metadata = {};
            }
            pLight.metadata.selected = node._selected;
        }
        this.viewer.updateLightWarnings();
        this.updatePreviewMesh(pLight);
    }

    delete3d(node: ITrackedClass<LightNode>) {
        if (this.babylonLightPreviewsMap) {
            const lightPositionGizmo = this.babylonLightPreviewsMap.get(node._dynamicId);
            if (!lightPositionGizmo) {
                throw Error('Light gizmo not found for light ' + node.id);
            }
            lightPositionGizmo.dispose();
            this.babylonLightPreviewsMap!.delete(node._dynamicId);
        }

        this.shadowManager.remove(this.get3d(node) as ShadowLight); //remove also disposes the shadow generator if it exists

        super.delete3d(node);
        this.viewer.updateLightWarnings();
    }

    protected updatePreviewMesh(light: Light) {
        let lightPositionGizmo = this.babylonLightPreviewsMap.get(light.id);
        const scene = this.gizmoLayer.utilityLayerScene;

        if (!lightPositionGizmo) {
            lightPositionGizmo = createLightVisualization(light, scene, Color3.Yellow());
            lightPositionGizmo.isPickable = false;
            this.babylonLightPreviewsMap.set(light.id, lightPositionGizmo);
        }

        if (this.showAllLightPreviews) {
            lightPositionGizmo.setEnabled(light.metadata.selected || light.isEnabled());
        } else {
            lightPositionGizmo.setEnabled(light.metadata.selected);
        }

        if (light instanceof HemisphericLight) {
            lightPositionGizmo.rotationQuaternion = rotationToVector(light.direction);
        } else if (light instanceof SpotLight) {
            lightPositionGizmo.position = this.getLightPosition(light);
            lightPositionGizmo.rotationQuaternion = rotationToVector(light.direction);
            lightPositionGizmo.scaling.set(
                Math.sin(light.angle / 2),
                Math.cos(light.angle / 2),
                Math.sin(light.angle / 2)
            );
        } else if (light instanceof DirectionalLight) {
            lightPositionGizmo.rotationQuaternion = rotationToVector(light.direction);
        } else if (light instanceof PointLight) {
            lightPositionGizmo.position = this.getLightPosition(light);
        }
    }

    protected rotateDirection(direction: Vector3, rotation: Quaternion) {
        direction.rotateByQuaternionToRef(rotation, direction);
        return direction;
    }

    protected getLightPosition(light: SpotLight | PointLight) {
        return light.parent
            ? Vector3.TransformCoordinates(light.position, light.parent.computeWorldMatrix(true).clone())
            : light.position;
    }
}
